import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { StockSync } from "models/StockSync";
import { PartType, Stock } from "operations/schema/schema";
import { RootState } from "store";
import { asyncMutations, mutationBuilder } from "./stock.mutations";
import { asyncQueries, queryBuilder } from "./stock.queries";

export interface State {
  stockEnquiry: PartType[];
  techStock: PartType[];
  orderStock: PartType[];
  stockList: Stock[];
  loadingStockEnquiry: boolean;
  loadingSyncStock: boolean;
  loadingOrderStock: boolean;
  stockToOrder: StockSync[];
  stockToMove: StockSync[];
  stockToUpdate: StockSync[];
}

interface Reducers extends SliceCaseReducers<State> {
  resetStockPage: (state: State) => State;
  updateStockToOrder: (state: State, action: PayloadAction<{ stock: StockSync }>) => State;
  updateStockToSync: (
    state: State,
    action: PayloadAction<{ stock: StockSync; moveStock: boolean }>,
  ) => State;
  removeStockToOrder: (state: State, action: PayloadAction<{ stock: StockSync }>) => State;
  removeStockToSync: (
    state: State,
    action: PayloadAction<{ stock: StockSync; moveStock: boolean }>,
  ) => State;
}

interface Selectors {
  selectStockList: (state: RootState) => Stock[];
}

export const initialState: State = {
  stockEnquiry: [],
  techStock: [],
  orderStock: [],
  stockList: [],
  loadingStockEnquiry: false,
  loadingSyncStock: false,
  loadingOrderStock: false,
  stockToOrder: [],
  stockToMove: [],
  stockToUpdate: [],
};

const reducers: Reducers = {
  resetStockPage(state) {
    state.stockToMove = [];
    state.stockToOrder = [];
    state.stockToUpdate = [];
    return state;
  },
  updateStockToOrder(state, { payload: { stock } }) {
    const index = state.stockToOrder.findIndex((s) => s.sync.articleNo === stock.sync.articleNo);
    let stockToOrder = state.stockToOrder;
    if (index !== -1) stockToOrder[index] = stock;
    else stockToOrder.push(stock);
    state.stockToOrder = [...stockToOrder];
    return state;
  },
  updateStockToSync(state, { payload: { stock, moveStock } }) {
    if (moveStock) {
      const index = state.stockToMove.findIndex((s) => s.sync.articleNo === stock.sync.articleNo);
      let stockToMove = state.stockToMove;
      if (index !== -1) stockToMove[index] = stock;
      else stockToMove.push(stock);
      state.stockToMove = [...stockToMove];
    } else {
      const index = state.stockToUpdate.findIndex((s) => s.sync.articleNo === stock.sync.articleNo);
      let stockToUpdate = state.stockToUpdate;
      if (index !== -1) stockToUpdate[index] = stock;
      else stockToUpdate.push(stock);
      state.stockToUpdate = [...stockToUpdate];
    }
    return state;
  },

  removeStockToOrder(state, { payload: { stock } }) {
    const stockToOrder = state.stockToOrder.filter(
      (s) => s.sync.articleNo !== stock.sync.articleNo,
    );
    state.stockToOrder = [...stockToOrder];
    return state;
  },
  removeStockToSync(state, { payload: { stock, moveStock } }) {
    if (moveStock) {
      const stockToMove = state.stockToMove.filter(
        (s) => s.sync.articleNo !== stock.sync.articleNo,
      );
      state.stockToMove = [...stockToMove];
    } else {
      const stockToUpdate = state.stockToUpdate.filter(
        (s) => s.sync.articleNo !== stock.sync.articleNo,
      );
      state.stockToUpdate = [...stockToUpdate];
    }
    return state;
  },
};

const selectors: Selectors = {
  selectStockList({ stock: { stockList } }) {
    return stockList;
  },
};

const storeBase = createSlice<State, Reducers>({
  name: "stock",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    queryBuilder(builder);
    mutationBuilder(builder);
  },
});

export default storeBase.reducer;
export const {
  resetStockPage,
  updateStockToSync,
  updateStockToOrder,
  removeStockToSync,
  removeStockToOrder,
} = storeBase.actions;
export const { selectStockList } = selectors;
export const { getStockEnquiry, getStockList, getTechStock, searchOrderStock } = asyncQueries;
export const { moveMobileStock, orderMobileStock, updateMobileStock } = asyncMutations;
